import axios from 'axios';
import authService from '../services/authService';
import { REACT_APP_BASE_PATH } from '../config/runtimeConfig';
import i18n from '../i18n/config';
import { parseBackendNotificationDetails, recipientDomainNotAllowedText } from '../utils/notification';
import { SHOW_UNEXPECTED_ERROR_EVENT } from '../components/UnexpectedErrorHost';
import { globalBusyTracker } from '../utils/globalBusyTracker';
import { requestVpnOtp, vpnOtpRequestFromError } from '../services/vpnOtpPrompt';
import { embedRequestHeaders, isEmbedRuntime } from '../services/embedRuntime';

let errorHandler: ((error: any) => void) | null = null;

/** Business/validation API faults → snackbar. Technical / 5xx / network → unexpected error panel. */
const isBusinessClientError = (status: number, data: unknown): boolean => {
    if (status === 428) return true;
    if (status < 400 || status >= 500) return false;
    const details = parseBackendNotificationDetails(data);
    if (details.businessErrorCode) return true;
    // 4xx without a code still treated as user-facing business/validation feedback.
    return status >= 400 && status < 500;
};

const emitUnexpectedError = (message: string) => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
        new CustomEvent(SHOW_UNEXPECTED_ERROR_EVENT, {
            detail: { message },
        })
    );
};

export const setGlobalErrorHandler = (handler: (error: any) => void) => {
    errorHandler = handler;
};

export const BASE_PATH = REACT_APP_BASE_PATH || 'https://api.socmanage.com/alert/v1';

const getHttpErrorDetails = (status: number) => {
    switch (status) {
        case 400:
            return {
                title: "Bad Request (400)",
                content: "The request is invalid. Please review the provided data and try again.",
            };
        case 401:
            return {
                title: "Unauthorized (401)",
                content: "Your session is not authorized. Please sign in again.",
            };
        case 403:
            return {
                title: "Forbidden (403)",
                content: "You do not have permission to perform this action.",
            };
        case 404:
            return {
                title: "Not Found (404)",
                content: "The requested resource could not be found.",
            };
        case 409:
            return {
                title: "Conflict (409)",
                content: "The request conflicts with the current state of the resource.",
            };
        case 422:
            return {
                title: "Unprocessable Entity (422)",
                content: "The request data is syntactically correct but semantically invalid.",
            };
        case 428:
            return {
                title: "Setup Required (428)",
                content: "Configure LLM and OpenSearch in workspace setup before using this feature.",
            };
        case 429:
            return {
                title: "Too Many Requests (429)",
                content: "Too many requests were sent in a short time. Please try again later.",
            };
        case 500:
            return {
                title: "Internal Server Error (500)",
                content: "The server encountered an unexpected condition.",
            };
        case 502:
            return {
                title: "Bad Gateway (502)",
                content: "The upstream server returned an invalid response.",
            };
        case 503:
            return {
                title: "Service Unavailable (503)",
                content: "The service is temporarily unavailable. Please try again later.",
            };
        case 504:
            return {
                title: "Gateway Timeout (504)",
                content: "The server took too long to respond.",
            };
        default:
            return {
                title: `Request Failed (${status})`,
                content: "The request could not be completed successfully.",
            };
    }
};

const api = axios.create({
    baseURL: BASE_PATH,
    headers: {
        'Content-Type': 'application/json',
    },
});

export const getAccessToken = async (): Promise<string | null> => authService.getAccessToken();

api.interceptors.request.use(
    async (config: any) => {
        // Track before awaiting the token so button busy-state can observe in-flight work.
        if (typeof window !== 'undefined' && !config.skipGlobalLoading) {
            globalBusyTracker.begin();
            config.__busyTracked = true;
        }
        try {
            const token = await getAccessToken();
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
            Object.assign(config.headers, embedRequestHeaders());
            return config;
        } catch (error) {
            if (config.__busyTracked) {
                globalBusyTracker.end();
                config.__busyTracked = false;
            }
            return Promise.reject(error);
        }
    },
    (error) => {
        if (typeof window !== 'undefined' && error.config?.__busyTracked) {
            globalBusyTracker.end();
            error.config.__busyTracked = false;
        }
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response: any) => {
        if (typeof window !== 'undefined' && response.config?.__busyTracked) {
            globalBusyTracker.end();
            response.config.__busyTracked = false;
        }
        return response;
    },
    (error: any) => {
        if (typeof window !== 'undefined' && error.config?.__busyTracked) {
            globalBusyTracker.end();
            error.config.__busyTracked = false;
        }

        if (authService.isLoggingOut()) {
            return Promise.reject(error);
        }

        if (error.response) {
            const status = error.response.status;

            // A VPN needs a one-time password: ask the user, then retry this request once.
            const vpnOtp = vpnOtpRequestFromError(status, error.response.data);
            if (vpnOtp && error.config && !error.config.__vpnOtpRetried) {
                return requestVpnOtp(vpnOtp).then((connected) => {
                    if (!connected) {
                        return Promise.reject(error);
                    }
                    error.config.__vpnOtpRetried = true;
                    return api.request(error.config);
                });
            }

            const backendDetails = parseBackendNotificationDetails(error.response.data);
            const domainWarning = recipientDomainNotAllowedText(error.response.data);

            // Callers that show the error themselves (e.g. the VPN OTP dialog) opt out of the global notice.
            // The embedded chat answers "setup incomplete" (428) in the conversation instead of a popup.
            const embedSetupRequired = status === 428 && isEmbedRuntime();
            if (status >= 400 && status < 600 && !error.config?.skipGlobalErrorNotification && !embedSetupRequired) {
                const details = getHttpErrorDetails(status);
                const content = domainWarning || backendDetails.message || details.content;

                if (domainWarning || isBusinessClientError(status, error.response.data)) {
                    if (errorHandler != null) {
                        const reason = backendDetails.reason || "API Request";
                        const info = backendDetails.info || `HTTP ${status}`;
                        const title = domainWarning
                            ? i18n.t("chat.mail.recipientDomainNotAllowedTitle")
                            : backendDetails.errorCode && backendDetails.reason
                                ? `${backendDetails.reason} (${backendDetails.errorCode})`
                                : details.title;

                        errorHandler({
                            status,
                            color: domainWarning ? "warning" : "error",
                            icon: domainWarning ? "warning" : "error",
                            title,
                            reason,
                            content,
                            info,
                            raw: error.response.data,
                        });
                    }
                } else {
                    emitUnexpectedError(content);
                }
            }

            if (status === 401) {
                void authService.getAccessToken().then((token) => {
                    if (!token) {
                        authService.login();
                    }
                });
            }

            if (status === 428 && typeof window !== "undefined") {
                // Menus stay browsable; incomplete setup only blocks ops (428).
                // Banner in App steers users to workspace settings — no hard redirect.
            }

            return Promise.reject(error);
        }

        let message = "An unexpected error occurred while preparing the request.";
        if (error.request) {
            message = "No response was received from the server. Please check your network connection.";
        } else if (error.message) {
            message = `Request setup error: ${error.message}`;
        }

        emitUnexpectedError(message);

        return Promise.reject(error);
    }
);
export const getDataWithConfig = async (endpoint: string, config: any) => {
    try {
        const response = await api.get(endpoint,config);
        return response.data;
    } catch (error) {
        console.error('GET error: %s', error);
        throw error;
    }
};

export const getData = async (endpoint: string, config: any = {}) => {
    try {
        const response = await api.get(endpoint, config);
        return response.data;
    } catch (error) {
        console.error('GET error: %s', error);
        throw error;
    }
};

export const postData = async (endpoint: string, data: any, config: any = {}) => {
    try {
        const response = await api.post(endpoint, data, config);
        return response.data;
    } catch (error: any) {
        console.error('POST error:', error);
        throw error;
    }
};

export const putData = async (endpoint: string, data: any) => {
    try {
        const response = await api.put(endpoint, data);
        return response.data;
    } catch (error) {
        console.error('PUT error: %s', error);
        throw error;
    }
};

export const patchData = async (endpoint: string, data: any) => {
    try {
        const response = await api.patch(endpoint, data);
        return response.data;
    } catch (error) {
        console.error('PATCH error: %s', error);
        throw error;
    }
};

export const deleteData = async (endpoint: string) => {
    try {
        const response = await api.delete(endpoint);
        return response.data;
    } catch (error) {
        console.error('DELETE error: %s', error);
        throw error;
    }
};

export const fileUpload = async (endpoint: string, formData: any, headers: any) => {
    try {
        const response = await api.post(endpoint, formData, headers);
        return response.data;
    } catch (error) {
        console.error('POST error: %s', error);
        throw error;
    }
};

